import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Car, Plus, RefreshCw, Search } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";

const VehiclesOtherBrands = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch vehicles (only "other" brand)
  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("brand", "other");
      if (searchQuery.trim()) {
        params.append("search", searchQuery.trim());
      }
      const res = await axios.get(`${API}/vehicles?${params.toString()}`, { withCredentials: true });
      setVehicles(res.data);
    } catch (e) {
      toast.error("Failed to fetch vehicles");
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  // Navigate to add vehicle page
  const handleAdd = () => {
    navigate("/vehicles/other-brands/new");
  };

  // Navigate to vehicle profile when clicking VIN
  const handleVinClick = (vehicleId) => {
    navigate(`/vehicles/other-brands/${vehicleId}`);
  };

  return (
    <div className="space-y-6" data-testid="vehicles-other-brands-page">
      {/* Standard Header */}
      <PageHeader
        icon={Car}
        title="Other Brands"
        subtitle="Manage non-Renault vehicles"
        onRefresh={fetchVehicles}
        primaryAction={{
          label: "Add Vehicle",
          onClick: handleAdd,
          icon: Plus,
          testId: "add-vehicle-btn",
        }}
        search={{
          value: searchQuery,
          onChange: setSearchQuery,
          onSubmit: fetchVehicles,
          placeholder: "Reg No / VIN / Engine No / Make / Phone / Customer",
          testId: "search-input",
        }}
      />

      {/* Vehicles Table */}
      <Card className="border border-gray-200 rounded-sm shadow-none">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-gray-200 bg-gray-50/50">
              <TableHead className="text-xs font-bold uppercase tracking-wider">Reg No</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wider">VIN</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wider">Brand</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wider">Model</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wider">Customer</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wider">Phone</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  <div className="w-6 h-6 border-2 border-gray-700 border-t-transparent rounded-full animate-spin mx-auto"></div>
                </TableCell>
              </TableRow>
            ) : vehicles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                  No vehicles found
                </TableCell>
              </TableRow>
            ) : (
              vehicles.map(v => (
                <TableRow 
                  key={v.vehicle_id} 
                  className="border-b border-gray-100 hover:bg-gray-50/50"
                >
                  <TableCell className="text-sm font-medium">{v.vehicle_reg_no}</TableCell>
                  <TableCell>
                    {v.vin ? (
                      <span
                        className="text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                        onClick={() => handleVinClick(v.vehicle_id)}
                        data-testid={`vin-${v.vehicle_id}`}
                      >
                        {v.vin}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="rounded-sm text-xs bg-gray-100">
                      {v.make || "-"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{v.model || "-"}</TableCell>
                  <TableCell className="text-sm">{v.customer_name || "-"}</TableCell>
                  <TableCell className="text-xs">{v.customer_phone || "-"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};

export default VehiclesOtherBrands;
